import moment from 'moment-timezone';

const validateBasicInfo = (formData) => {
  const errors = [];

  if (!formData.name?.trim()) {
    errors.push('Campaign name is required');
  }

  if (!formData.account) {
    errors.push('Account selection is required');
  }

  if (!formData.crm_campaign) {
    errors.push('CRM Campaign selection is required');
  }

  if (!formData.start_date) {
    errors.push('Start date is required');
  }

  if (!formData.is_ongoing && !formData.end_date) {
    errors.push('End date is required for non-ongoing campaigns');
  }

  if (formData.start_date && formData.end_date && !formData.is_ongoing) {
    const start = moment(formData.start_date);
    const end = moment(formData.end_date);
    if (end.isBefore(start)) {
      errors.push('End date must be after start date');
    }
  }

  return errors;
};

const validateMessageConfig = (formData) => {
  const errors = [];
  const channel = formData.channel;
  const channelConfig = formData[`${channel}_config`];

  // Validate channel config (voice can rely on step-level configs)
  if (!channelConfig && channel !== 'voice') {
    errors.push('Channel configuration is required');
    return errors;
  }

  // Validate from_endpoint (only for non-voice channels)
  if (channel !== 'voice' && !channelConfig.from_endpoint) {
    errors.push('From endpoint is required');
  }

  // Validate message steps
  const messageSteps = formData.drip_schedule?.message_steps || [];
  if (messageSteps.length === 0) {
    errors.push('At least one message step is required');
  } else {
    messageSteps.forEach((step, index) => {
      const stepConfig = step[`${channel}_config`];
      
      // Validate step order
      if (step.order !== index + 1) {
        errors.push(`Message step ${index + 1} has incorrect order`);
      }

      // Validate delay settings
      if (step.delay_units <= 0) {
        errors.push(`Message step ${index + 1} delay must be positive`);
      }
      if (!['minutes', 'hours', 'days'].includes(step.delay_unit_type)) {
        errors.push(`Message step ${index + 1} has invalid delay unit type`);
      }

      // Validate content or template (skip for voice as content comes from platform config)
      if (channel !== 'voice' && !step.template && !stepConfig?.content?.trim()) {
        errors.push(`Message step ${index + 1} requires either a template or content`);
      }
      
      // Validate voice_config exists for voice steps
      if (channel === 'voice' && !step.voice_config) {
        errors.push(`Message step ${index + 1}: Voice configuration is required`);
      }

      // Validate step-specific channel config (or voice_config for voice)
      if (stepConfig || (channel === 'voice' && step.voice_config)) {
        switch (channel) {
          case 'email':
            if (!stepConfig.subject?.trim() && !step.template) {
              errors.push(`Message step ${index + 1} requires an email subject`);
            }
            break;
          case 'sms':
          case 'chat':
            if (!stepConfig.content?.trim() && !step.template) {
              errors.push(`Message step ${index + 1} requires message content`);
            }
            break;
          case 'voice':
            // Voice validation is handled separately since content comes from platform config
            // Check for step-level voice_config first, then fall back to channel config
            const voiceConfig = step.voice_config || stepConfig;
            if (!voiceConfig.platform) {
              errors.push(`Message step ${index + 1}: Voice platform is required`);
            }
            
            // Validate from_endpoint for voice (step-level)
            if (!voiceConfig.from_endpoint) {
              errors.push(`Message step ${index + 1}: From endpoint is required`);
            }
            
            // Platform-specific validations
            if (voiceConfig.platform === 'bland_ai') {
              if (!voiceConfig.platform_config?.pathway_id) {
                errors.push(`Message step ${index + 1}: Bland AI pathway ID is required`);
              }
            }
            
            if (!voiceConfig.voice_id) {
              errors.push(`Message step ${index + 1}: Voice ID is required`);
            }
            break;
        }
      }
    });
  }

  return errors;
};

const validateReminderMessageConfig = (formData) => {
  const errors = [];
  const channel = formData.channel;

  // Validate reminder times
  const reminderTimes = formData.reminder_schedule?.reminder_times || [];
  if (reminderTimes.length === 0) {
    errors.push('At least one reminder time is required');
  } else {
    reminderTimes.forEach((reminderTime, timeIndex) => {
      const messages = reminderTime.messages || [];
      
      if (messages.length === 0) {
        errors.push(`Reminder time ${timeIndex + 1}: At least one message is required`);
      } else {
        messages.forEach((message, messageIndex) => {
          const messageConfig = message[`${channel}_config`];
          
          // Validate content or template (skip for voice as content comes from platform config)
          if (channel !== 'voice' && !message.template && !message.content?.trim()) {
            errors.push(`Reminder time ${timeIndex + 1}, Message ${messageIndex + 1}: Content is required`);
          }
          
          // Validate voice_config exists for voice messages
          if (channel === 'voice' && !message.voice_config) {
            errors.push(`Reminder time ${timeIndex + 1}, Message ${messageIndex + 1}: Voice configuration is required`);
          }

          // Validate channel-specific configurations
          if (messageConfig || (channel === 'voice' && message.voice_config)) {
            switch (channel) {
              case 'email':
                if (!messageConfig.from_endpoint) {
                  errors.push(`Reminder time ${timeIndex + 1}, Message ${messageIndex + 1}: From endpoint is required`);
                }
                if (!messageConfig.subject?.trim() && !message.template) {
                  errors.push(`Reminder time ${timeIndex + 1}, Message ${messageIndex + 1}: Email subject is required`);
                }
                break;
              case 'sms':
              case 'chat':
                if (!messageConfig.from_endpoint) {
                  errors.push(`Reminder time ${timeIndex + 1}, Message ${messageIndex + 1}: From endpoint is required`);
                }
                if (!messageConfig.content?.trim() && !message.template) {
                  errors.push(`Reminder time ${timeIndex + 1}, Message ${messageIndex + 1}: Message content is required`);
                }
                break;
              case 'voice':
                // Voice-specific validation
                const voiceConfig = message.voice_config;
                if (!voiceConfig.platform) {
                  errors.push(`Reminder time ${timeIndex + 1}, Message ${messageIndex + 1}: Voice platform is required`);
                }
                
                // Validate from_endpoint for voice
                if (!voiceConfig.from_endpoint) {
                  errors.push(`Reminder time ${timeIndex + 1}, Message ${messageIndex + 1}: From endpoint is required`);
                }
                
                // Platform-specific validations
                if (voiceConfig.platform === 'bland_ai') {
                  if (!voiceConfig.platform_config?.task && !voiceConfig.platform_config?.pathway_id) {
                    errors.push(`Reminder time ${timeIndex + 1}, Message ${messageIndex + 1}: Bland AI task or pathway ID is required`);
                  }
                }
                
                if (voiceConfig.platform === 'vapi') {
                  if (!voiceConfig.platform_config?.voice_id) {
                    errors.push(`Reminder time ${timeIndex + 1}, Message ${messageIndex + 1}: VAPI voice ID is required`);
                  }
                }
                
                if (voiceConfig.platform === 'elevenlabs') {
                  if (!voiceConfig.platform_config?.voice_id) {
                    errors.push(`Reminder time ${timeIndex + 1}, Message ${messageIndex + 1}: ElevenLabs voice ID is required`);
                  }
                }
                
                if (voiceConfig.platform === 'twilio') {
                  if (!voiceConfig.platform_config?.voice_id) {
                    errors.push(`Reminder time ${timeIndex + 1}, Message ${messageIndex + 1}: Twilio voice ID is required`);
                  }
                }
                
                // Common voice validations
                if (!voiceConfig.voice_id) {
                  errors.push(`Reminder time ${timeIndex + 1}, Message ${messageIndex + 1}: Voice ID is required`);
                }
                
                if (voiceConfig.temperature < 0 || voiceConfig.temperature > 1) {
                  errors.push(`Reminder time ${timeIndex + 1}, Message ${messageIndex + 1}: Temperature must be between 0 and 1`);
                }
                
                if (voiceConfig.max_duration <= 0) {
                  errors.push(`Reminder time ${timeIndex + 1}, Message ${messageIndex + 1}: Max duration must be positive`);
                }
                break;
            }
          }
        });
      }
    });
  }

  return errors;
};

const validateScheduleSettings = (formData) => {
  const errors = [];
  const schedule = formData.drip_schedule;

  if (!schedule) {
    errors.push('Schedule settings are required');
    return errors;
  }

  // Validate timezone
  if (!schedule.timezone || !moment.tz.zone(schedule.timezone)) {
    errors.push('Valid timezone is required');
  }

  // Validate business hours
  if (schedule.business_hours_only) {
    if (!schedule.start_time || !schedule.end_time) {
      errors.push('Business hours start and end times are required');
    } else {
      const start = moment(schedule.start_time, 'HH:mm');
      const end = moment(schedule.end_time, 'HH:mm');
      if (!start.isValid() || !end.isValid()) {
        errors.push('Invalid business hours format');
      } else if (end.isSameOrBefore(start)) {
        errors.push('Business hours end time must be after start time');
      }
    }
  }

  // Validate message step timing
  const messageSteps = schedule.message_steps || [];
  if (messageSteps.length > 0) {
    const totalDelay = messageSteps.reduce((total, step) => {
      const multiplier = {
        minutes: 1,
        hours: 60,
        days: 1440
      }[step.delay_unit_type] || 0;
      return total + (step.delay_units * multiplier);
    }, 0);

    if (totalDelay === 0) {
      errors.push('Total campaign duration must be greater than 0');
    }
  }

  return errors;
};

const validateReminderScheduleSettings = (formData) => {
  const errors = [];
  const schedule = formData.reminder_schedule;

  if (!schedule) {
    errors.push('Reminder schedule settings are required');
    return errors;
  }

  // Validate timezone
  if (!schedule.timezone || !moment.tz.zone(schedule.timezone)) {
    errors.push('Valid timezone is required');
  }

  // Validate reminder times
  const reminderTimes = schedule.reminder_times || [];
  if (reminderTimes.length === 0) {
    errors.push('At least one reminder time is required');
  } else {
    reminderTimes.forEach((reminderTime, index) => {
      if (schedule.use_relative_schedule) {
        // Validate relative schedule
        const hasTimeValue = (reminderTime.days_before_relative > 0) || 
                           (reminderTime.hours_before > 0) || 
                           (reminderTime.minutes_before > 0);
        if (!hasTimeValue) {
          errors.push(`Reminder time ${index + 1}: At least one time value (days, hours, or minutes) is required`);
        }
      } else {
        // Validate absolute schedule
        if (!reminderTime.days_before || reminderTime.days_before <= 0) {
          errors.push(`Reminder time ${index + 1}: Days before must be positive`);
        }
        if (!reminderTime.time) {
          errors.push(`Reminder time ${index + 1}: Time is required`);
        } else {
          const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
          if (!timeRegex.test(reminderTime.time)) {
            errors.push(`Reminder time ${index + 1}: Invalid time format (HH:MM)`);
          }
        }
      }
    });
  }

  return errors;
};

const validateStep = (activeStep, formData, campaignType = 'drip') => {
  let errors = [];

  switch (activeStep) {
    case 0: // Basic Information
      errors = validateBasicInfo(formData);
      break;
    case 1: // Message Configuration
      if (campaignType === 'reminder') {
        errors = validateReminderMessageConfig(formData);
      } else {
        errors = validateMessageConfig(formData);
      }
      break;
    case 2: // Schedule Settings
      if (campaignType === 'reminder') {
        errors = validateReminderScheduleSettings(formData);
      } else {
        errors = validateScheduleSettings(formData);
      }
      break;
    case 3: // Test
      // No validation needed for test step
      break;
    case 4: // Review & Activate
      // Validate all steps
      if (campaignType === 'reminder') {
        errors = [
          ...validateBasicInfo(formData),
          ...validateReminderMessageConfig(formData),
          ...validateReminderScheduleSettings(formData)
        ];
      } else {
        errors = [
          ...validateBasicInfo(formData),
          ...validateMessageConfig(formData),
          ...validateScheduleSettings(formData)
        ];
      }
      break;
    default:
      errors = ['Invalid step'];
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export { validateStep }; 